import type { SparklinePoint } from '../types';

type Props = {
  points: SparklinePoint[];
  width?: number;
  height?: number;
  // True when the parent archive is in an inactive state
  // (done / paused / terminated / error / auth_failed). The sparkline
  // still shows its historical shape but in grey, so active crawls
  // dominate the eye in the table.
  muted?: boolean;
};

// Match the on-archive ProgressChart so the eye doesn't have to
// re-key colors between the list view and the detail view.
const COLOR_PAGES = '#2a7';
const COLOR_QUEUED = '#36c';
const COLOR_PAGES_MUTED = '#bcd4c4';
const COLOR_QUEUED_MUTED = '#c0c8d8';

/**
 * Tiny two-line sparkline: pages (green) + queued (blue) on a shared
 * y-scale. No axes — meant to be glanced at, not read.
 *
 * Empty / single-point inputs render as a flat dash so the column doesn't
 * jump around as archives accumulate samples.
 */
export default function Sparkline({ points, width = 100, height = 22, muted = false }: Props) {
  if (points.length < 2) {
    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <line
          x1={2} x2={width - 2} y1={height / 2} y2={height / 2}
          stroke="#ccc" strokeWidth={1}
        />
      </svg>
    );
  }

  const xs = points.map((p) => Date.parse(p.sampled_at));
  const xMin = xs[0];
  const xMax = xs[xs.length - 1];
  const xSpan = Math.max(xMax - xMin, 1);

  const yMax = Math.max(
    1,
    ...points.map((p) => Math.max(p.pages_fetched, p.queued_count)),
  );

  const padX = 1;
  const padY = 2;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const sx = (t: number) => padX + ((t - xMin) / xSpan) * innerW;
  const sy = (v: number) => padY + innerH - (v / yMax) * innerH;

  const pagesPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(xs[i]).toFixed(1)} ${sy(p.pages_fetched).toFixed(1)}`)
    .join(' ');
  const queuedPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(xs[i]).toFixed(1)} ${sy(p.queued_count).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      width={width}
      height={height}
      style={{ display: 'block' }}
      aria-label="24h trend: pages vs queued"
    >
      <path d={queuedPath} fill="none" stroke={muted ? COLOR_QUEUED_MUTED : COLOR_QUEUED} strokeWidth={1.5} />
      <path d={pagesPath} fill="none" stroke={muted ? COLOR_PAGES_MUTED : COLOR_PAGES} strokeWidth={1.5} />
    </svg>
  );
}
